/**
 * Core domain events — the only channel by which capability reaches core.
 *
 * Core emits; modules subscribe. Core holds no reference to any module and has
 * no idea who is listening (Principle III). scripts/check-boundaries.ts fails
 * the build if core ever imports a module directly.
 *
 * EVERY payload carries `opId`. That is what lets a module make its own side
 * effect idempotent under sync replay — the inventory module writes
 * UNIQUE(op_id, ingredient_id) on its stock movement, so a replayed sale
 * physically cannot deduct twice (FR-068).
 *
 * See contracts/module-contract.md.
 */

import type { IsoTimestamp } from "../time";

// Structural shapes only. Core events must not leak a module's types, or two
// modules could couple to each other through core.

export interface SaleFinalizedEvent {
  opId: string;
  saleId: string;
  documentNumber: string;
  terminalId: string;
  shiftId: string;
  staffId: string;
  orderType: string;
  hasCashTender: boolean;
  lines: ReadonlyArray<{
    lineId: string;
    menuItemId: string;
    variantId: string | null;
    modifierIds: readonly string[];
    quantity: number;
    note: string | null;
    /** What was charged for the line, after every discount. Integer minor units. */
    lineTotal: number;
    /** The module that granted the line (a line grant), or null. */
    grantedByModule: string | null;
  }>;
  /**
   * Context modules attached to the cart, keyed by module id (cart
   * annotations). Delivered here and nowhere else: core never stores it.
   */
  annotations: Readonly<Record<string, unknown>>;
  createdAt: IsoTimestamp;
}

export interface SaleVoidedEvent {
  opId: string;
  saleId: string;
  voidId: string;
  staffId: string;
  authorizedBy: string;
  reason: string;
  createdAt: IsoTimestamp;
}

export interface SaleRefundedEvent {
  opId: string;
  saleId: string;
  refundId: string;
  staffId: string;
  authorizedBy: string;
  hasCashRefund: boolean;
  lines: ReadonlyArray<{ saleLineId: string; quantity: number }>;
  createdAt: IsoTimestamp;
}

export interface ShiftOpenedEvent {
  opId: string;
  shiftId: string;
  terminalId: string;
  staffId: string;
  createdAt: IsoTimestamp;
}

export interface ShiftClosedEvent {
  opId: string;
  shiftId: string;
  terminalId: string;
  staffId: string;
  createdAt: IsoTimestamp;
}

/**
 * A no-sale drawer open was AUTHORIZED.
 *
 * This is a core cash-control fact and is recorded by core whether or not any
 * peripheral module exists (FR-041b). The peripherals module subscribes to it
 * in order to attempt a physical release — but the authorization, its reason,
 * and its actor are core's, and the module never duplicates them.
 */
export interface DrawerNoSaleAuthorizedEvent {
  opId: string;
  auditEntryId: string;
  shiftId: string;
  terminalId: string;
  staffId: string;
  authorizedBy: string | null;
  reason: string;
  createdAt: IsoTimestamp;
}

export interface CatalogChangedEvent {
  opId: string;
  entity: string;
  id: string;
  createdAt: IsoTimestamp;
}

export interface CoreEventMap {
  "sale.finalized": SaleFinalizedEvent;
  "sale.voided": SaleVoidedEvent;
  "sale.refunded": SaleRefundedEvent;
  "shift.opened": ShiftOpenedEvent;
  "shift.closed": ShiftClosedEvent;
  "drawer.noSaleAuthorized": DrawerNoSaleAuthorizedEvent;
  "catalog.changed": CatalogChangedEvent;
}

export type CoreEventName = keyof CoreEventMap;

export type CoreEventHandler<K extends CoreEventName> = (
  event: CoreEventMap[K],
) => void | Promise<void>;

export interface SubscriberFailure {
  event: CoreEventName;
  moduleId: string;
  error: unknown;
  at: IsoTimestamp;
}

export interface EventBusOptions {
  /** Where a failing subscriber is reported. Never throws into the emitter. */
  onSubscriberError?: (failure: SubscriberFailure) => void;
}

interface Registration {
  moduleId: string;
  handler: CoreEventHandler<CoreEventName>;
}

export class CoreEventBus {
  readonly #handlers = new Map<CoreEventName, Registration[]>();
  readonly #onSubscriberError: NonNullable<EventBusOptions["onSubscriberError"]>;

  constructor(options: EventBusOptions = {}) {
    this.#onSubscriberError =
      options.onSubscriberError ??
      ((failure) => {
        console.error(
          `[events] subscriber "${failure.moduleId}" failed on "${failure.event}"`,
          failure.error,
        );
      });
  }

  on<K extends CoreEventName>(event: K, moduleId: string, handler: CoreEventHandler<K>): () => void {
    const list = this.#handlers.get(event) ?? [];
    const registration: Registration = {
      moduleId,
      handler: handler as CoreEventHandler<CoreEventName>,
    };
    list.push(registration);
    this.#handlers.set(event, list);

    return () => {
      const current = this.#handlers.get(event);
      if (!current) return;
      const index = current.indexOf(registration);
      if (index >= 0) current.splice(index, 1);
    };
  }

  /** Remove every subscription a module holds. Used when a module is disabled. */
  offModule(moduleId: string): void {
    for (const [event, list] of this.#handlers) {
      this.#handlers.set(
        event,
        list.filter((r) => r.moduleId !== moduleId),
      );
    }
  }

  /**
   * Emit to every subscriber, isolating failures.
   *
   * A subscriber that throws is recorded and the emitter continues. This is
   * Principle III taken to its obvious corollary: if disabling every module must
   * leave a working till, then a module that is merely BROKEN must also leave a
   * working till. A failing inventory deduction may never stop a café taking
   * money.
   */
  async emit<K extends CoreEventName>(event: K, payload: CoreEventMap[K]): Promise<void> {
    const list = this.#handlers.get(event);
    if (!list || list.length === 0) return;

    await Promise.all(
      [...list].map(async (registration) => {
        try {
          await registration.handler(payload);
        } catch (error) {
          this.#onSubscriberError({
            event,
            moduleId: registration.moduleId,
            error,
            at: new Date().toISOString(),
          });
        }
      }),
    );
  }

  subscriberCount(event: CoreEventName): number {
    return this.#handlers.get(event)?.length ?? 0;
  }
}

export const coreEvents = new CoreEventBus();
